// =============================================================================
// Register Client Use Case
//
// Orchestrates the full client registration workflow as a single application
// operation, decoupled from the UI. Each step fails loudly with a meaningful
// error so the chain stops cleanly instead of writing a partial record.
//
// Steps:
//   1. Cognito registration
//   2. Informativ / CBC credit user creation (with ERROR tag validation)
//   3. Stripe customer creation
//   4. Format and write to DynamoDB
// =============================================================================

import axios from "axios";
import convert from "xml-js";
import { registerCognitoUser } from "./cognito";
import { addCreditUser } from "../informativ/creditUsers";
import { extractInformativError, extractCusId } from "../informativ/validateResponse";
import { addNewCustomer } from "../stripe/customers";
import { userDataToClient } from "../format/users";
import { putUser } from "../dynamo/mutations";
import { REGISTRATION_ERRORS } from "../../constants/registrationErrors";
import { Client } from "../types/UserTypes";
import { userTypes } from "../types";

const IP_LOOKUP_URL = "https://api.ipify.org?format=json";

const generateFallbackUid = (): string =>
  `${Math.random().toFixed(4)}-${Math.random().toFixed(4)}`.replaceAll(".", "e");

export const resolveClientIp = async (): Promise<string> => {
  try {
    const { data } = await axios.get(IP_LOOKUP_URL);
    return data.ip;
  } catch {
    return "";
  }
};

interface RegisterClientParams {
  formData: userTypes.BaseUserInfo & { password: string };
  company: string;
  member: string;
}

/**
 * Runs the full registration workflow. Returns the persisted Client on success.
 * Throws a user-facing error message if any step fails.
 */
export const registerClient = async ({
  formData,
  company,
  member,
}: RegisterClientParams): Promise<Client> => {
  const { email, password, name, phone } = formData;

  // Step 1: Cognito registration
  let cognitoResult;
  try {
    cognitoResult = await registerCognitoUser(email, password);
  } catch {
    throw new Error(REGISTRATION_ERRORS.COGNITO_FAILURE);
  }

  const uid = cognitoResult?.userSub || generateFallbackUid();

  // Step 2: Informativ / CBC credit user creation
  let cusId: string | undefined;
  try {
    const ip = await resolveClientIp();
    const informativResult = await addCreditUser({
      uid,
      name,
      email,
      password,
      ip,
    });

    const convertedResult = convert.xml2js(informativResult, { compact: true });

    // Explicitly check the CBC ERROR tag before treating this as a success
    const informativError = extractInformativError(convertedResult as any);
    if (informativError) {
      throw new Error(informativError);
    }

    cusId = extractCusId(convertedResult as any);
  } catch (error: any) {
    // Surface a user-facing message but keep the underlying detail for logs
    console.error("Informativ registration failed:", error?.message);
    throw new Error(REGISTRATION_ERRORS.INFORMATIV_FAILURE);
  }

  // Step 3: Stripe customer creation
  let customer;
  try {
    customer = await addNewCustomer(name, email, phone);
  } catch {
    throw new Error(REGISTRATION_ERRORS.STRIPE_FAILURE);
  }

  // Step 4: Format and persist to DynamoDB
  try {
    const newClient = userDataToClient(formData, uid, company, member);
    newClient.metadata.cus_id = cusId || "";
    newClient.metadata.stripeId = customer?.id;

    await putUser(newClient);
    //@ts-ignore
    return newClient;
  } catch {
    throw new Error(REGISTRATION_ERRORS.DYNAMO_FAILURE);
  }
};